import { ReactNode, useEffect, useMemo, useState } from "react";
import { Button, ButtonProps, Checkbox, Popover } from "antd";
import {
  DownOutlined,
  InfoCircleOutlined,
  UpOutlined
} from "@ant-design/icons";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";

export type ExportFormatOption = {
  key: string;
  title: ReactNode;
  description?: ReactNode;
  /** Title/description shown while the option is selected. Excel's title defaults to "Excel". */
  selectedTitle?: ReactNode;
  selectedDescription?: ReactNode;
  isExcel?: boolean;
  disabled?: boolean;
  /** When false, hide Include Comments for this option. Defaults to true. */
  supportsIncludeComments?: boolean;
  testId?: string;
};

export type ExportSelection = {
  key: string;
  includeComments: boolean;
  excelOptions?: ExcelExportOptions;
};

type Props = {
  options: ExportFormatOption[];
  onProceed: (selection: ExportSelection) => void;
  excelTableName: string;
  showIncludeComments?: boolean;
  /** Replaces the Include Comments checkbox with this note; comments are then always included. */
  includeCommentsNote?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  buttonType?: ButtonProps["type"];
  buttonTestId?: string;
  buttonTitle?: string;
};

function ExportOptionsPopover({
  options,
  onProceed,
  excelTableName,
  showIncludeComments = true,
  includeCommentsNote,
  disabled = false,
  loading = false,
  buttonType = "primary",
  buttonTestId,
  buttonTitle
}: Readonly<Props>) {
  const anyOptionSupportsComments =
    showIncludeComments &&
    options.some((option) => option.supportsIncludeComments !== false);
  const singleOption = options.length === 1 ? options[0] : undefined;
  const exportsOnButtonClick =
    !anyOptionSupportsComments && !!singleOption && !singleOption.isExcel;
  const defaultSelectedKey = options[0]?.key;

  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(
    defaultSelectedKey
  );
  const [includeComments, setIncludeComments] = useState(false);
  const [showExcelSettings, setShowExcelSettings] = useState(true);
  const [freezeHeaders, setFreezeHeaders] = useState(true);
  const [freezeColumns, setFreezeColumns] = useState(true);
  const [exportAsExcelTable, setExportAsExcelTable] = useState(true);

  useEffect(() => {
    if (
      selectedKey !== defaultSelectedKey &&
      !options.some((option) => option.key === selectedKey)
    ) {
      setSelectedKey(defaultSelectedKey);
    }
  }, [options, selectedKey, defaultSelectedKey]);

  const resetSelection = () => {
    setSelectedKey(defaultSelectedKey);
    setIncludeComments(false);
    setShowExcelSettings(true);
    setFreezeHeaders(true);
    setFreezeColumns(true);
    setExportAsExcelTable(true);
  };

  const selectedOption = useMemo(
    () => options.find((option) => option.key === selectedKey),
    [options, selectedKey]
  );

  const showCommentsForSelection =
    showIncludeComments && selectedOption?.supportsIncludeComments !== false;

  const handleProceed = () => {
    if (!selectedOption || selectedOption.disabled) return;
    const selection: ExportSelection = {
      key: selectedOption.key,
      includeComments:
        showCommentsForSelection && (!!includeCommentsNote || includeComments)
    };
    if (selectedOption.isExcel) {
      selection.excelOptions = {
        freezeHeaders,
        freezeColumns,
        exportAsExcelTable: exportAsExcelTable
          ? { name: excelTableName }
          : false
      };
    }
    setOpen(false);
    onProceed(selection);
  };

  const onOptionClick = (option: ExportFormatOption) =>
    setSelectedKey(option.key);

  const renderOption = (option: ExportFormatOption) => {
    const isSelected = option.key === selectedKey;
    const selectedTitle =
      option.selectedTitle ?? (option.isExcel ? "Excel" : undefined);
    const title = isSelected ? (selectedTitle ?? option.title) : option.title;
    const description = isSelected ? option.selectedDescription : undefined;

    return (
      <div key={option.key}>
        <button
          type="button"
          disabled={option.disabled}
          onClick={() => onOptionClick(option)}
          className={`w-full flex flex-col px-3 py-1.5 rounded-md border-0 text-left leading-[22px] ${
            isSelected
              ? "bg-ciq-neutral-25"
              : "bg-transparent hover:bg-ciq-gray-50"
          } ${option.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
          data-testid={option.testId}
          aria-pressed={isSelected}
        >
          <span className="text-sm text-ciq-black-a88">{title}</span>
          {description && (
            <span className="text-xs text-ciq-gray-900">{description}</span>
          )}
        </button>

        {isSelected && option.isExcel && (
          <div className="px-3 pt-1 pb-2">
            <Button
              type="link"
              size="small"
              className="px-0 text-xs"
              onClick={() => setShowExcelSettings((prev) => !prev)}
              data-testid="export-options-toggle-excel-settings"
            >
              {showExcelSettings ? "Hide" : "Show"} Excel Settings{" "}
              {showExcelSettings ? <UpOutlined /> : <DownOutlined />}
            </Button>
            {showExcelSettings && (
              <div className="flex flex-col space-y-1.5 pl-1 pt-1">
                <Checkbox
                  checked={freezeHeaders}
                  onChange={(e) => setFreezeHeaders(e.target.checked)}
                >
                  Freeze Headers
                </Checkbox>
                <Checkbox
                  checked={freezeColumns}
                  onChange={(e) => setFreezeColumns(e.target.checked)}
                >
                  Freeze Pinned Columns
                </Checkbox>
                <Checkbox
                  checked={exportAsExcelTable}
                  onChange={(e) => setExportAsExcelTable(e.target.checked)}
                >
                  Export as Excel Table
                </Checkbox>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const content = (
    <div className="w-[265px]" data-testid="export-options-popover">
      <div className="flex flex-col gap-0.5">{options.map(renderOption)}</div>

      {showCommentsForSelection && (
        <div className="mx-3 mt-1.5 border-0 border-t border-solid border-ciq-gray-300" />
      )}

      {showCommentsForSelection && includeCommentsNote && (
        <div
          className="flex items-center gap-2 px-3 pt-3 text-xs text-ciq-gray-850"
          data-testid="export-options-comments-note"
        >
          <InfoCircleOutlined className="!text-ciq-gray-850" />
          <span>{includeCommentsNote}</span>
        </div>
      )}

      {showCommentsForSelection && !includeCommentsNote && (
        <div className="pt-3 px-0.5">
          <Checkbox
            checked={includeComments}
            onChange={(e) => setIncludeComments(e.target.checked)}
            className="!items-start [&_.ant-checkbox]:!self-start [&_.ant-checkbox]:!mt-1 [&>span:last-child]:!pl-2.5"
            data-testid="export-options-include-comments"
          >
            <span className="text-sm leading-[22px] text-ciq-black-a88">
              Include Comments
            </span>
          </Checkbox>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-5">
        <Button
          onClick={() => setOpen(false)}
          data-testid="export-options-cancel"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleProceed}
          disabled={!selectedOption || selectedOption.disabled}
          data-testid="export-options-proceed"
        >
          Proceed
        </Button>
      </div>
    </div>
  );

  const exportButton = (
    <Button
      type={buttonType}
      disabled={disabled}
      loading={loading}
      title={buttonTitle}
      data-testid={buttonTestId}
      onClick={
        exportsOnButtonClick
          ? () => {
              if (!singleOption.disabled) {
                onProceed({ key: singleOption.key, includeComments: false });
              }
            }
          : undefined
      }
    >
      {exportsOnButtonClick ? (
        <>Export as {singleOption.title}</>
      ) : (
        <>
          Export <DownOutlined />
        </>
      )}
    </Button>
  );

  if (exportsOnButtonClick) return exportButton;

  return (
    <Popover
      content={content}
      open={open && !disabled}
      onOpenChange={(nextOpen) => {
        if (nextOpen) resetSelection();
        setOpen(nextOpen);
      }}
      trigger="click"
      placement="bottomRight"
      arrow={false}
    >
      {exportButton}
    </Popover>
  );
}

export default ExportOptionsPopover;
